import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { login } from "api/cookie_auth";
import { Button } from "components/button";
import { IconInput } from "components/form/icon-input";
import { PasswordInput } from "components/form/password-input";
import { paths } from "config/paths";
import globalStyles from "index.module.css";
import alexLiveSrc from "assets/alex-live.svg";
import logoSrc from "assets/logo-2.svg";
import EmailIcon from "assets/person.svg?react";
import PasswordIcon from "assets/password.svg?react";
import { isValidEmail } from "./is-valid-email";
import styles from "./index.module.css";
import { TwoFactorDialog } from "./two-factor-dialog";
import { toast } from "sonner";

const forgotPasswordUrl = import.meta.env.DEV
  ? "https://develop.voyc.ai/forgot-password"
  : "https://app.voyc.ai/forgot-password";

export const Login = () => {
  const initialEmail = import.meta.env.DEV
    ? (window.sessionStorage.getItem("login:email") ?? "")
    : "";
  const initialPassword = import.meta.env.DEV
    ? (window.sessionStorage.getItem("login:password") ?? "")
    : "";

  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [twoFactorErrorMessage, setTwoFactorErrorMessage] = useState("");
  const [is2faLoading, setIs2faLoading] = useState(false);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedEmail = email.trim();
    setErrorMessage("");

    if (!(trimmedEmail && password)) {
      setErrorMessage("Both email and password are required");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage("Invalid email address provided");
      return;
    }

    setIsLoading(true);
    const [, error] = await login(trimmedEmail, password);
    setIsLoading(false);

    if (error) {
      if (error === "2FA_REQUIRED") {
        setShowTwoFactorDialog(true);
        setTwoFactorErrorMessage("");
        return;
      }

      setErrorMessage(error);
      return;
    }

    if (import.meta.env.DEV) {
      window.sessionStorage.setItem("login:email", trimmedEmail);
      window.sessionStorage.setItem("login:password", password);
    }

    const from = location.state?.from || paths.audioSettings;
    navigate(from, { replace: true });
  };

  const handleTwoFactorSubmit = async (otpCode: string) => {
    const trimmedEmail = email.trim();
    setTwoFactorErrorMessage("");
    setIs2faLoading(true);

    const [, error] = await login(trimmedEmail, password, otpCode);
    setIs2faLoading(false);

    if (error) {
      setTwoFactorErrorMessage(error);
      return;
    }

    if (import.meta.env.DEV) {
      window.sessionStorage.setItem("login:email", trimmedEmail);
      window.sessionStorage.setItem("login:password", password);
    }
    setShowTwoFactorDialog(false);

    const from = location.state?.from || paths.audioSettings;
    navigate(from, { replace: true });
  };

  const handleTwoFactorDialogClose = (open: boolean) => {
    if (!is2faLoading) {
      setShowTwoFactorDialog(open);
      if (!open) {
        setTwoFactorErrorMessage("");
      }
    }
  };

  useEffect(() => {
    if (errorMessage) {
      toast(errorMessage);
    }
  }, [errorMessage]);

  return (
    <div className={globalStyles.container}>
      <div className={styles.logoContainer}>
        <img alt="Voyc" className={styles.logo} src={alexLiveSrc} />
      </div>

      <form
        aria-label="Login Form"
        className={globalStyles.formContainer}
        noValidate
        onSubmit={handleLogin}
      >
        <h1 className={styles.formTitle}>Welcome Back!</h1>

        <div className={styles.formContent}>
          <IconInput
            aria-label="Email"
            autoComplete="email"
            containerClassName={styles.inputGroup}
            icon={<EmailIcon />}
            iconAlt="Email"
            id="email-input"
            inputClassName={styles.input}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            style={{ marginBottom: 24 }}
            type="email"
            value={email}
            label="Email"
          />

          <PasswordInput
            label="Password"
            aria-label="Password"
            autoComplete="current-password"
            containerClassName={styles.inputGroup}
            icon={<PasswordIcon />}
            iconAlt="Password"
            iconClassName={styles.inputIcon}
            id="password-input"
            inputClassName={styles.input}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            style={{ marginBottom: 12 }}
            toggleButtonClassName={styles.passwordToggle}
            value={password}
          />

          <div className={styles.actions}>
            <a
              className={styles.forgotPassword}
              href={forgotPasswordUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Forgot Password?
            </a>
          </div>

          <Button
            className={styles.loginButton}
            disabled={!(email.trim() && password)}
            isLoading={isLoading}
            type="submit"
            variant="white"
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </div>
        <div className={styles.footer}>
          <p>Powered By</p>
          <div className={styles.logoContainer}>
            <img alt="Voyc" className={styles.logo} src={logoSrc} />
          </div>
        </div>
      </form>

      <TwoFactorDialog
        errorMessage={twoFactorErrorMessage}
        isLoading={is2faLoading}
        onOpenChange={handleTwoFactorDialogClose}
        onSubmit={handleTwoFactorSubmit}
        open={showTwoFactorDialog}
      />
    </div>
  );
};
